import { useState } from 'react'
import type { FC, ReactNode } from 'react'
import {
  Box,
  Button,
  ButtonBase,
  CircularProgress,
  Collapse,
  Paper,
  Skeleton,
  Typography,
} from '@mui/material'
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import type { BffCrmPersonClaimsRetrieveResponse } from '@fireflyhealth/api'

import { DetailGrid, EmptyState, StatusPill, type Tone } from '~/shared/ui'
import type { PatientUser } from '~/shared/types'
import { useGetPersonClaims } from './PersonClaimsService'

// Shared column template so the header row and every claim row line up. Provider keeps a floor so a
// narrow panel can't crush it to nothing (which pushed its text, and the unbreakable PROVIDER
// header, over the claim number column); below TABLE_MIN_WIDTH the table scrolls sideways instead.
const GRID_COLUMNS = '108px minmax(150px, 1fr) 150px 124px 96px 28px'
const TABLE_MIN_WIDTH = 760

// Server-owned shape — reuse the generated response type rather than re-declaring it.
type Claim = BffCrmPersonClaimsRetrieveResponse['claims'][number]

interface ClaimsProps {
  patient: PatientUser
}

/**
 * Display formatting for the raw values the claims API returns — decimal strings ("200.00") and
 * date-only ISO strings ("2024-03-15"). Only presentation lives here: every amount, and the
 * Finalized/Processing `displayStatus`, is computed server-side and rendered verbatim.
 *
 * The wire format stays raw deliberately. One shared service builds this payload for both the CRM
 * and the member app (which formats the same fields for its own surface), so pre-formatting it
 * server-side would lock both surfaces to one presentation and make the amounts unusable for
 * sorting or arithmetic.
 */
const usdFormatter = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  minimumFractionDigits: 0,
  maximumFractionDigits: 2,
})

// The one piece of backend encoding this file decodes: the service renders an unpopulated (null)
// amount as the sentinel "0", while a real zero comes back quantized as "0.00" — so an em dash
// means "not populated" and "$0" means an actual zero.
const formatUsd = (value: string): string =>
  value === '0' ? '—' : usdFormatter.format(Number(value) || 0)

// A discount reduces the charge, so render a populated non-zero amount as a negative.
const formatDiscount = (value: string): string => {
  if (value === '0') return '—'
  const amount = Number(value) || 0
  return amount > 0 ? `− ${usdFormatter.format(amount)}` : usdFormatter.format(0)
}

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']

// `incurred` is a date-only ISO string ("2024-03-15") or ""; format without a Date so a local
// timezone can't shift the day, and without date-fns (the CRM's pinned v1 uses different tokens).
const formatIncurred = (iso: string): string => {
  const [date = ''] = iso.split('T')
  const [year, month, day] = date.split('-').map(Number)
  if (!year || !month || !day) return '—'
  return `${MONTHS[month - 1]} ${day}, ${year}`
}

// displayStatus is computed server-side ("Finalized" / "Processing"); the client only picks the tone.
const displayStatusTone = (displayStatus: string): Tone =>
  displayStatus === 'Finalized' ? 'success' : 'neutral'

/**
 * One label/value money row in a detail breakdown card. `divider` draws a hairline below it;
 * `emphasize` promotes it to the bold total (heavier top rule); `discount` renders it as a negative.
 */
const MoneyRow: FC<{
  label: string
  value: string
  divider?: boolean
  emphasize?: boolean
  discount?: boolean
}> = ({ label, value, divider, emphasize, discount }) => (
  <Box
    sx={{
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      py: 1,
      ...(divider ? { borderBottom: '1px solid', borderColor: 'divider' } : {}),
      ...(emphasize ? { borderTop: '2px solid', borderColor: 'divider' } : {}),
    }}
  >
    <Typography
      variant="body2"
      sx={{
        color: emphasize ? 'text.primary' : 'text.secondary',
        fontWeight: emphasize ? 600 : 400,
      }}
    >
      {label}
    </Typography>
    <Typography variant="body2" sx={{ fontWeight: emphasize ? 700 : 500, color: 'text.primary' }}>
      {discount ? formatDiscount(value) : formatUsd(value)}
    </Typography>
  </Box>
)

/** Heading above one of the detail cards. */
const CardHeading: FC<{ children: ReactNode }> = ({ children }) => (
  <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
    {children}
  </Typography>
)

/** The token a code is shown as, in a detail card: diagnosis and ineligible codes share it. */
const CodeChip: FC<{ code: string }> = ({ code }) => (
  <Box
    component="span"
    sx={theme => ({
      display: 'inline-block',
      fontFamily: theme.typography.fontFamilyMono,
      fontSize: '1.2rem',
      fontWeight: 600,
      bgcolor: theme.palette.tones.info.bg,
      color: theme.palette.tones.info.fg,
      px: 1,
      py: 0.25,
      borderRadius: '6px',
    })}
  >
    {code}
  </Box>
)

/** A single claim: a table row that expands in place to the full app-parity detail. */
const ClaimRow: FC<{ claim: Claim; memberName: string; isFirst: boolean }> = ({
  claim,
  memberName,
  isFirst,
}) => {
  const [expanded, setExpanded] = useState(false)
  const { provider, totals, dates, displayStatus } = claim
  // `provider.displayName` is resolved server-side (rendering provider, else facility), so which
  // field identifies the provider is decided in one place. The facility only earns a second line
  // when it isn't already the label.
  const providerPrimary = provider.displayName || '—'
  const providerSecondary =
    provider.officeName && provider.officeName !== provider.displayName ? provider.officeName : ''

  return (
    <Box
      data-testid={`claim-${claim.claimNumber}`}
      sx={isFirst ? undefined : { borderTop: '1px solid', borderColor: 'divider' }}
    >
      {/* ButtonBase keeps the row keyboard-focusable and toggle-able; children are spans so the
          markup stays valid inside a <button>. */}
      <ButtonBase
        onClick={() => setExpanded(value => !value)}
        aria-expanded={expanded}
        aria-label={`Claim ${claim.claimNumber}`}
        data-testid={`claim-toggle-${claim.claimNumber}`}
        sx={{
          display: 'grid',
          gridTemplateColumns: GRID_COLUMNS,
          alignItems: 'center',
          gap: 1.5,
          width: '100%',
          textAlign: 'left',
          px: 2,
          py: 1.5,
          '&:hover': { bgcolor: 'action.hover' },
        }}
      >
        <Box component="span">
          <StatusPill tone={displayStatusTone(displayStatus)} label={displayStatus} dot />
        </Box>
        <Box component="span" sx={{ display: 'block', minWidth: 0 }}>
          <Typography
            component="span"
            variant="body2"
            noWrap
            sx={{ display: 'block', fontWeight: 600 }}
          >
            {providerPrimary}
          </Typography>
          {providerSecondary && (
            <Typography
              component="span"
              variant="caption"
              noWrap
              sx={{ color: 'text.secondary', display: 'block' }}
            >
              {providerSecondary}
            </Typography>
          )}
        </Box>
        <Typography
          component="span"
          variant="caption"
          noWrap
          sx={theme => ({ color: 'text.secondary', fontFamily: theme.typography.fontFamilyMono })}
        >
          {claim.claimNumber || '—'}
        </Typography>
        <Typography component="span" variant="body2">
          {formatIncurred(dates.incurred)}
        </Typography>
        <Typography component="span" variant="body2" sx={{ fontWeight: 600, textAlign: 'right' }}>
          {formatUsd(totals.charge)}
        </Typography>
        <Box component="span" sx={{ display: 'flex', justifyContent: 'flex-end' }}>
          <KeyboardArrowRightIcon
            fontSize="small"
            sx={{
              color: 'text.secondary',
              transition: 'transform 0.15s',
              transform: expanded ? 'rotate(90deg)' : 'none',
            }}
          />
        </Box>
      </ButtonBase>

      <Collapse in={expanded} unmountOnExit>
        <Box sx={{ px: 2, pt: 0.5, pb: 2.5, bgcolor: 'action.hover' }}>
          <Box data-testid={`claim-detail-${claim.claimNumber}`} sx={{ pt: 1.5, pb: 2 }}>
            <DetailGrid
              columns={3}
              items={[
                { label: 'Member', value: memberName },
                { label: 'Claim number', value: claim.claimNumber || '—', mono: true },
                { label: 'Dates incurred', value: formatIncurred(dates.incurred) },
              ]}
            />
          </Box>

          {/* All four cards are grid items rather than two stacked columns, so the second row
              starts at one baseline instead of trailing whichever card above it is taller. */}
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
              gap: 2,
              alignItems: 'start',
            }}
          >
            <Box>
              <CardHeading>Price breakdown</CardHeading>
              <Paper variant="outlined" sx={{ px: 2, bgcolor: 'background.paper' }}>
                <MoneyRow label="Provider charge" value={totals.charge} divider />
                <MoneyRow label="Plan discount" value={totals.discount} discount divider />
                <MoneyRow label="Plan paid" value={totals.paid} divider />
                <MoneyRow label="Not allowed" value={totals.ineligible} />
                <MoneyRow
                  label="Patient responsibility"
                  value={totals.patientResponsibility}
                  emphasize
                />
              </Paper>
            </Box>

            <Box>
              <CardHeading>Applied to plan</CardHeading>
              <Paper variant="outlined" sx={{ px: 2, bgcolor: 'background.paper' }}>
                <MoneyRow label="Copay" value={totals.copay} divider />
                <MoneyRow label="Deductible" value={totals.deductible} divider />
                <MoneyRow label="Coinsurance" value={totals.coinsurance} divider />
                <MoneyRow label="Out-of-pocket" value={totals.outOfPocket} />
              </Paper>
            </Box>

            {/* Why the payer disallowed the Not-allowed amount; either half can be absent. */}
            {(claim.ineligibleCode || claim.ineligibleDescription) && (
              <Box data-testid={`claim-ineligible-${claim.claimNumber}`}>
                <CardHeading>Ineligible reasons</CardHeading>
                <Paper
                  variant="outlined"
                  sx={{
                    p: 2,
                    bgcolor: 'background.paper',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 1.5,
                  }}
                >
                  {claim.ineligibleCode && (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
                      <CodeChip code={claim.ineligibleCode} />
                    </Box>
                  )}
                  {claim.ineligibleDescription && (
                    <Typography variant="body2" sx={{ lineHeight: 1.5 }}>
                      {claim.ineligibleDescription}
                    </Typography>
                  )}
                </Paper>
              </Box>
            )}

            {/* The diagnosis code block only renders when the claim carries an ICD-10 code. */}
            {claim.cpgCode && (
              <Box data-testid={`claim-code-${claim.claimNumber}`}>
                <CardHeading>Code</CardHeading>
                <Paper variant="outlined" sx={{ p: 2, bgcolor: 'background.paper' }}>
                  <CodeChip code={claim.cpgCode} />
                  {claim.primaryICDDescription && (
                    <Typography variant="body2" sx={{ mt: 1.5, lineHeight: 1.5 }}>
                      {claim.primaryICDDescription}
                    </Typography>
                  )}
                </Paper>
              </Box>
            )}
          </Box>
        </Box>
      </Collapse>
    </Box>
  )
}

const HEADER_CELLS = ['Status', 'Provider', 'Claim #', 'Date incurred', 'Charge', ''] as const

/** The table's column-header row, shared by the loaded table and the loading skeleton. */
const ClaimsHeaderRow: FC = () => (
  <Box
    sx={{
      display: 'grid',
      gridTemplateColumns: GRID_COLUMNS,
      gap: 1.5,
      px: 2,
      py: 1,
      bgcolor: 'action.hover',
      borderBottom: '1px solid',
      borderColor: 'divider',
    }}
  >
    {HEADER_CELLS.map((label, index) => (
      <Typography
        // Header labels are fixed and unique enough by position; index keys are fine here.
        key={label || `spacer-${index}`}
        variant="caption"
        noWrap
        sx={{
          color: 'text.secondary',
          fontWeight: 600,
          textTransform: 'uppercase',
          letterSpacing: '0.04em',
          textAlign: label === 'Charge' ? 'right' : 'left',
        }}
      >
        {label}
      </Typography>
    ))}
  </Box>
)

/**
 * Table shell: the outlined card clips the rounded corners, and the inner track holds the grid at
 * its minimum width so a narrow panel scrolls the table instead of collapsing its columns.
 */
const ClaimsTable: FC<{ children: ReactNode }> = ({ children }) => (
  <Paper variant="outlined" data-testid="claims-table-scroll" sx={{ overflow: 'auto' }}>
    <Box data-testid="claims-table-track" sx={{ minWidth: TABLE_MIN_WIDTH }}>
      {children}
    </Box>
  </Paper>
)

/**
 * Placeholder rows for the initial fetch. Reuses the real header and column template so the table
 * doesn't shift when the claims land. Only `isLoading` renders this — on "Load more" the infinite
 * query sets `isFetchingNextPage` instead, so an already-populated table is never replaced.
 */
const ClaimsSkeleton: FC = () => (
  <Box sx={{ p: 3 }} data-testid="claims-loading">
    <ClaimsTable>
      <ClaimsHeaderRow />
      {Array.from({ length: 6 }).map((_, index) => (
        <Box
          key={index}
          sx={{
            display: 'grid',
            gridTemplateColumns: GRID_COLUMNS,
            gap: 1.5,
            alignItems: 'center',
            px: 2,
            py: 1.5,
            ...(index === 0 ? {} : { borderTop: '1px solid', borderColor: 'divider' }),
          }}
        >
          <Skeleton variant="rounded" width={76} height={20} />
          <Skeleton variant="text" width="60%" />
          <Skeleton variant="text" width="80%" />
          <Skeleton variant="text" width="70%" />
          <Skeleton variant="text" />
          <Box />
        </Box>
      ))}
    </ClaimsTable>
  </Box>
)

const Claims: FC<ClaimsProps> = ({ patient }) => {
  const personId = patient.person?.id
  const {
    data,
    isPending: isLoading,
    error,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage,
  } = useGetPersonClaims({ personId })

  if (isLoading) {
    return <ClaimsSkeleton />
  }

  if (error) {
    return (
      <EmptyState
        icon={<InfoOutlinedIcon />}
        title="Couldn't load claims"
        caption="Something went wrong fetching this member's claims. Please try again."
      />
    )
  }

  const claims = data?.pages.flatMap(page => page.claims) ?? []

  if (claims.length === 0) {
    return (
      <EmptyState
        icon={<InfoOutlinedIcon />}
        title="No claims"
        caption="This member has no claims history to display yet."
      />
    )
  }

  const memberName = `${patient.first_name ?? ''} ${patient.last_name ?? ''}`.trim() || '—'

  return (
    <Box sx={{ p: 3 }}>
      <ClaimsTable>
        <ClaimsHeaderRow />

        {claims.map((claim, index) => (
          <ClaimRow
            key={claim.claimNumber}
            claim={claim}
            memberName={memberName}
            isFirst={index === 0}
          />
        ))}
      </ClaimsTable>

      {hasNextPage && (
        <Box sx={{ textAlign: 'center', mt: 1.5 }}>
          {/* The label stays the action; the spinner carries the in-flight state, so the button
              keeps its width and its accessible name doesn't change mid-interaction. */}
          <Button
            onClick={() => void fetchNextPage()}
            disabled={isFetchingNextPage}
            startIcon={
              isFetchingNextPage ? <CircularProgress size={16} color="inherit" /> : undefined
            }
            data-testid="claims-load-more"
          >
            Load more
          </Button>
        </Box>
      )}
    </Box>
  )
}

export default Claims
